<template>
  <div class="wage-Process-container">
    <!-- 좌측 사이드바 -->
    <div class="steps-sidebar">
      <div class="sidebar-header">
        <div class="sidebar-title-row">
          <h1 class="sidebar-title">임금 관리</h1>
          <div class="year-month-selector-wrapper">
            <YearMonthSelector v-model="selectedYearMonth" label="정산 년월" />
          </div>
        </div>
        <div class="payment-date-wrapper" :class="{ 'highlight-blink': isPaymentDateHighlighted }">
          <label class="payment-date-label">임금지급일</label>
          <input 
            type="date" 
            v-model="paymentDate" 
            class="payment-date-input"
            @change="handlePaymentDateChange"
          />
        </div>
        <div class="payment-date-wrapper san-rate-wrapper">
          <label class="payment-date-label">산재보험요율</label>
          <div class="san-rate-input-group">
            <input 
              type="text" 
              v-model="sanRateDisplay" 
              class="payment-date-input san-rate-input"
              placeholder="예) 0.0068"
              @input="onSanRateInput"
              @blur="onSanRateBlur"
            />
            <span class="san-rate-unit" v-if="sanRatePercent">{{ sanRatePercent }}</span>
          </div>
        </div>
        
      </div>

      <div class="steps-list">
        <!-- 1단계 -->
        <div class="step-item" :class="{
          'is-completed': step1Completed,
          'is-active': selectedStep === 1,
          'is-locked': !canAccessStep(1)
        }" @click="selectStep(1)">
          <div class="step-item-indicator">
            <div class="step-number">
              <v-icon v-if="step1Completed" color="white" size="16">mdi-check</v-icon>
              <span v-else>1</span>
            </div>
          </div>
          <div class="step-item-content">
            <h3 class="step-item-title">사원 정보</h3>
            <p class="step-item-desc">사원 목록 업로드</p>
          </div>
        </div>

        <!-- 2단계 -->
        <div class="step-item" :class="{
          'is-completed': step2Completed,
          'is-active': selectedStep === 2,
          'is-locked': !canAccessStep(2)
        }" @click="selectStep(2)">
          <div class="step-item-indicator">
            <div class="step-number">
              <v-icon v-if="step2Completed" color="white" size="16">mdi-check</v-icon>
              <span v-else>2</span>
            </div>
          </div>
          <div class="step-item-content">
            <h3 class="step-item-title">청구 내역</h3>
            <p class="step-item-desc">청구내역 파일 업로드</p>
          </div>
        </div>

        <!-- 3단계 -->
        <div class="step-item" :class="{
          'is-completed': step3Completed,
          'is-active': selectedStep === 3,
          'is-locked': !canAccessStep(3)
        }" @click="selectStep(3)">
          <div class="step-item-indicator">
            <div class="step-number">
              <v-icon v-if="step3Completed" color="white" size="16">mdi-check</v-icon>
              <span v-else>3</span>
            </div>
          </div>
          <div class="step-item-content">
            <h3 class="step-item-title">요양보호사 급여</h3>
            <p class="step-item-desc">세금, 공제 설정</p>
          </div>
        </div>

        <!-- 4단계 -->
        <div class="step-item" :class="{
          'is-completed': step4Completed,
          'is-active': selectedStep === 4,
          'is-locked': !canAccessStep(4)
        }" @click="selectStep(4)">
          <div class="step-item-indicator">
            <div class="step-number">
              <v-icon v-if="step4Completed" color="white" size="16">mdi-check</v-icon>
              <span v-else>4</span>
            </div>
          </div>
          <div class="step-item-content">
            <h3 class="step-item-title">사회복지사/센터장 급여</h3>
            <p class="step-item-desc">시급, 수당 설정</p>
          </div>
        </div>

        <!-- 5단계 -->
        <div class="step-item" :class="{
          'is-completed': step5Completed,
          'is-active': selectedStep === 5,
          'is-locked': !canAccessStep(5)
        }" @click="selectStep(5)">
          <div class="step-item-indicator">
            <div class="step-number">
              <v-icon v-if="step5Completed" color="white" size="16">mdi-check</v-icon>
              <span v-else>5</span>
            </div>
          </div>
          <div class="step-item-content">
            <h3 class="step-item-title">임금관리결과</h3>
            <p class="step-item-desc">결과 확인 및 출력</p>
          </div>
        </div>
      </div>
    </div>

    <!-- 우측 메인 콘텐츠 -->
    <div class="main-content">
      <transition name="slide-fade" mode="out-in">
        <StepOne v-if="selectedStep === 1" key="step1" :employees="employees" :step-completed="employees.length > 0"
          :is-confirmed="step1Completed" :is-confirming="confirmingStep === 1" @upload="openMemberUpload"
          @confirm="confirmWageStep(1)" @release="releaseWageStep(1)" @boss-updated="fetchEmployees" />
        <StepTwo v-else-if="selectedStep === 2" key="step2" :uploaded-files-count="uploadedFilesCount"
          :billing-data="billingData" :billing-detail-data="billingDetailData"
          :is-loading="isLoadingBilling" :active-year="billingYear" :active-month="billingMonth"
          :is-confirmed="step2Completed" :is-confirming="confirmingStep === 2" @uploaded="handleBillingUploaded"
          @confirm="confirmWageStep(2)" @release="releaseWageStep(2)" />
        <StepThree v-else-if="selectedStep === 3" key="step3" :step-completed="step3Completed"
          :is-confirmed="step3Completed" :is-confirming="confirmingStep === 3" :year-month="selectedYearMonth"
          :san-rate="sanRate"
          @open-settings="goToPayrollSettings" @confirm="confirmWageStep(3)" @release="releaseWageStep(3)" />
        <StepFour v-else-if="selectedStep === 4" key="step4" :step-completed="step4Completed"
          :is-confirmed="step4Completed" :is-confirming="confirmingStep === 4"
          @confirm="confirmWageStep(4)" @release="releaseWageStep(4)" />
        <StepFinal v-else-if="selectedStep === 5" key="stepFinal" :step-completed="step5Completed"
          :is-confirmed="step4Completed" :is-confirming="confirmingStep === 4" :year-month="selectedYearMonth"
          :payment-date="paymentDate" :san-rate="sanRate"
          @view-result="goToPayrollResult" @confirm="confirmWageStep(4)" @release="releaseWageStep(4)"
          @require-payment-date="highlightPaymentDate" />
      </transition>
    </div>

    <!-- 사원 업로드 모달 -->
    <MemberUploadModal v-model="showMemberUpload" @uploaded="handleMemberUploaded" />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import axios from 'axios';
import MemberUploadModal from '@/components/employee/MemberUploadModal.vue';
import StepOne from '@/components/wage/process/StepOne.vue';
import StepTwo from '@/components/wage/process/StepTwo.vue';
import StepThree from '@/components/wage/process/StepThree.vue';
import StepFour from '@/components/wage/process/StepFour.vue';
import StepFinal from '@/components/wage/process/StepFinal.vue';
import YearMonthSelector from '@/components/common/YearMonthSelector.vue';

const router = useRouter();
const store = useStore();

// 단계 완료 상태
const step1Completed = ref(false);
const step2Completed = ref(false);
const step3Completed = ref(false);
const step4Completed = ref(false);
const step5Completed = computed(() => step4Completed.value);
const confirmingStep = ref<number | null>(null);

// 선택된 단계
const selectedStep = ref(1);

// 총 단계 수
const totalSteps = 5;

// 완료된 단계 수
const completedCount = computed(() => {
  let count = 0;
  if (step1Completed.value) count++;
  if (step2Completed.value) count++;
  if (step3Completed.value) count++;
  if (step4Completed.value) count++;
  if (step5Completed.value) count++;
  return count;
});

// 프로그레스 서클 계산
const progressOffset = computed(() => {
  const circumference = 2 * Math.PI * 26;
  const progress = (completedCount.value / totalSteps) * circumference;
  return circumference - progress;
});

// 사원 업로드 모달
const showMemberUpload = ref(false);

// 일괄 업로드 관련
const uploadedFilesCount = ref(0);

// 청구내역 데이터
const billingData = ref<any[]>([]);
const billingDetailData = ref<any[]>([]);
const isLoadingBilling = ref(false);
// 초기값: 한 달 전 연월
const getLastMonth = () => {
  const now = new Date();
  const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return {
    year: lastMonth.getFullYear(),
    month: lastMonth.getMonth() + 1
  };
};
const lastMonth = getLastMonth();
const billingYear = ref(lastMonth.year);
const billingMonth = ref(lastMonth.month);
const selectedYearMonth = ref(`${billingYear.value}${String(billingMonth.value).padStart(2, '0')}`);

// 사원 정보
const employees = ref<any[]>([]);
const isLoadingEmployees = ref(false);

// 임금지급일
const paymentDate = ref('');
const isPaymentDateHighlighted = ref(false);

// 산재보험요율 (기관별, 소수 형태로 저장 예: 0.0068 = 0.68%)
const sanRate = ref<number | null>(null);
const sanRateDisplay = ref('');

// 퍼센트 표시 (소수 → 퍼센트 문자열)
const sanRatePercent = computed(() => {
  const val = parseFloat(sanRateDisplay.value);
  if (isNaN(val) || val <= 0) return '';
  return `${(val * 100).toFixed(2)}%`;
});

const onSanRateInput = (e: Event) => {
  const input = e.target as HTMLInputElement;
  // 숫자와 소수점만 허용
  let val = input.value.replace(/[^0-9.]/g, '');
  // 소수점 하나만 허용
  const parts = val.split('.');
  if (parts.length > 2) {
    val = parts[0] + '.' + parts.slice(1).join('');
  }
  sanRateDisplay.value = val;
};

const onSanRateBlur = () => {
  const val = parseFloat(sanRateDisplay.value);
  if (!isNaN(val) && val > 0) {
    sanRate.value = val; // 소수 그대로 저장 (예: 0.0068)
    sanRateDisplay.value = val.toString();
  } else {
    sanRate.value = null;
    sanRateDisplay.value = '';
  }
  handleSanRateChange();
};

// 기본 임금지급일 계산 (selectedYearMonth + 1개월의 10일)
const getDefaultPaymentDate = (): string => {
  if (!selectedYearMonth.value || selectedYearMonth.value.length !== 6) {
    return '';
  }
  const year = parseInt(selectedYearMonth.value.substring(0, 4));
  const month = parseInt(selectedYearMonth.value.substring(4, 6));
  
  // +1개월 계산
  let nextYear = year;
  let nextMonth = month + 1;
  if (nextMonth > 12) {
    nextMonth = 1;
    nextYear += 1;
  }
  
  return `${nextYear}-${String(nextMonth).padStart(2, '0')}-10`;
};

// Wage Process 상태
const wageProcessStatus = ref<any>(null);
const isLoadingWageProcess = ref(false);

// 단계 접근 가능 여부
const canAccessStep = (step: number): boolean => {
  if (step === 1) return true;
  if (step === 2) return step1Completed.value;
  if (step === 3) return step1Completed.value && step2Completed.value;
  if (step === 4) return step1Completed.value && step2Completed.value && step3Completed.value;
  if (step === 5) return (
    step1Completed.value &&
    step2Completed.value &&
    step3Completed.value &&
    step4Completed.value
  );
  return false;
};

// 단계 선택
const selectStep = (step: number) => {
  if (!canAccessStep(step)) return;
  selectedStep.value = step;

  // 1단계 선택 시 사원 정보 가져오기
  if (step === 1) {
    fetchEmployees();
  }
  if (step === 2) {
    fetchBillingData();
  }
};

// Wage Process 상태 조회 - 가장 높은 접근 가능한 단계로 이동
const syncSelectedStepWithStatus = () => {
  // 가장 높은 접근 가능한 단계 찾기
  for (let step = totalSteps; step >= 1; step--) {
    if (canAccessStep(step)) {
      selectedStep.value = step;
      return;
    }
  }
  selectedStep.value = 1;
};

const fetchWageProcessStatus = async () => {
  const user = store.state.user;
  if (!user || !user.m_sisul_code) {
    return;
  }

  try {
    isLoadingWageProcess.value = true;
    const response = await axios.get('/api/wage-process/status', {
      params: {
        sisulCode: user.m_sisul_code,
        yearMonth: selectedYearMonth.value
      },
      withCredentials: true
    });

    if (response.data.success && response.data.data) {
      wageProcessStatus.value = response.data.data;
      // 테이블의 완료 상태를 적용
      step1Completed.value = wageProcessStatus.value.wp_step1_completed === 1;
      step2Completed.value = wageProcessStatus.value.wp_step2_completed === 1;
      step3Completed.value = wageProcessStatus.value.wp_step3_completed === 1;
      step4Completed.value = wageProcessStatus.value.wp_step4_completed === 1;
    } else {
      // 프로세스가 없으면 초기화
      wageProcessStatus.value = null;
      step1Completed.value = false;
      step2Completed.value = false;
      step3Completed.value = false;
      step4Completed.value = false;
    }
    syncSelectedStepWithStatus();
  } catch (error) {
    console.error('Wage Process 상태 조회 오류:', error);
  } finally {
    isLoadingWageProcess.value = false;
  }
};

// 사원 정보 가져오기
const fetchEmployees = async () => {
  const user = store.state.user;
  if (!user || !user.m_sisul_code) {
    return;
  }

  try {
    isLoadingEmployees.value = true;
    const response = await axios.get(`/api/files/member-data/${user.m_sisul_code}`, {
      withCredentials: true
    });

    if (response.data.success) {
      employees.value = response.data.data || [];
    } else {
      employees.value = [];
    }
  } catch (error) {
    console.error('사원 정보 조회 오류:', error);
    employees.value = [];
  } finally {
    isLoadingEmployees.value = false;
  }
};

const fetchBillingData = async () => {
  const user = store.state.user;
  if (!user || !user.m_sisul_code) {
    return;
  }

  try {
    isLoadingBilling.value = true;
    const year = billingYear.value;
    const month = billingMonth.value;
    
    const [billingResponse, billingDetailResponse] = await Promise.all([
      axios.get(
        `/api/files/data/${year}/${month}/${encodeURIComponent('청구내역')}/${user.m_sisul_code}`,
        { withCredentials: true }
      ),
      axios.get(
        `/api/files/data/${year}/${month}/${encodeURIComponent('청구내역상세')}/${user.m_sisul_code}`,
        { withCredentials: true }
      )
    ]);

    billingData.value = billingResponse.data?.data?.rows || [];
    billingDetailData.value = billingDetailResponse.data?.data?.rows || [];

    const hasBilling = billingData.value.length > 0;
    const hasBillingDetail = billingDetailData.value.length > 0;

    uploadedFilesCount.value = (hasBilling ? 1 : 0) + (hasBillingDetail ? 1 : 0);
  } catch (error) {
    console.error('청구 데이터 조회 오류:', error);
    billingData.value = [];
    billingDetailData.value = [];
    uploadedFilesCount.value = 0;
  } finally {
    isLoadingBilling.value = false;
  }
};

// 지급일 조회
const fetchPaymentDate = async () => {
  const user = store.state.user;
  if (!user || !user.m_sisul_code || !selectedYearMonth.value) {
    return;
  }

  try {
    const response = await axios.get('/api/wage-process/payment-date', {
      params: {
        sisulCode: user.m_sisul_code,
        yearMonth: selectedYearMonth.value
      },
      withCredentials: true
    });

    if (response.data.success && response.data.data) {
      paymentDate.value = response.data.data.wg_date || '';
      // 산재보험요율 로드 (소수 형태 그대로, 예: 0.0068)
      if (response.data.data.wg_san_rate !== null && response.data.data.wg_san_rate !== undefined && response.data.data.wg_san_rate > 0) {
        sanRate.value = response.data.data.wg_san_rate;
        sanRateDisplay.value = response.data.data.wg_san_rate.toString();
      } else {
        // 현재 월에 산재보험요율이 없으면 과거 월에서 가져오기
        await fetchPreviousSanRate();
      }
    } else {
      // 데이터가 없으면 기본값으로 selectedYearMonth + 1개월의 10일 설정
      paymentDate.value = getDefaultPaymentDate();
      // 과거 월의 산재보험요율 가져오기
      await fetchPreviousSanRate();
    }
  } catch (error) {
    console.error('지급일 조회 오류:', error);
    paymentDate.value = getDefaultPaymentDate();
    sanRate.value = null;
    sanRateDisplay.value = '';
  }
};

// 과거 월의 산재보험요율 조회 및 자동 저장
const fetchPreviousSanRate = async () => {
  const user = store.state.user;
  if (!user || !user.m_sisul_code || !selectedYearMonth.value) {
    sanRate.value = null;
    sanRateDisplay.value = '';
    return;
  }

  const currentYear = parseInt(selectedYearMonth.value.substring(0, 4));
  const currentMonth = parseInt(selectedYearMonth.value.substring(4, 6));

  // 최근 12개월까지 역순으로 검색
  for (let i = 1; i <= 12; i++) {
    let targetYear = currentYear;
    let targetMonth = currentMonth - i;
    
    if (targetMonth <= 0) {
      targetYear--;
      targetMonth += 12;
    }
    
    const targetYearMonth = `${targetYear}${String(targetMonth).padStart(2, '0')}`;
    
    try {
      const response = await axios.get('/api/wage-process/payment-date', {
        params: {
          sisulCode: user.m_sisul_code,
          yearMonth: targetYearMonth
        },
        withCredentials: true
      });
      
      if (response.data.success && response.data.data) {
        const prevSanRate = response.data.data.wg_san_rate;
        if (prevSanRate !== null && prevSanRate !== undefined && prevSanRate > 0) {
          sanRate.value = prevSanRate;
          sanRateDisplay.value = prevSanRate.toString();
          console.log(`과거 산재보험요율 적용: ${targetYearMonth}월 ${prevSanRate}`);
          
          // 과거 산재보험요율을 현재 월에 자동 저장
          await saveSanRateToCurrentMonth(prevSanRate);
          return;
        }
      }
    } catch (error) {
      // 에러 발생해도 계속 검색
    }
  }
  
  // 과거 데이터에서도 찾지 못함
  sanRate.value = null;
  sanRateDisplay.value = '';
};

// 산재보험요율을 현재 월에 저장 (과거 데이터 자동 적용 시)
const saveSanRateToCurrentMonth = async (rate: number) => {
  const user = store.state.user;
  if (!user || !user.m_sisul_code || !selectedYearMonth.value) {
    return;
  }

  try {
    // 현재 지급일이 없으면 기본값 사용
    const currentPaymentDate = paymentDate.value || getDefaultPaymentDate();
    
    await axios.post('/api/wage-process/payment-date', {
      sisulCode: user.m_sisul_code,
      yearMonth: selectedYearMonth.value,
      paymentDate: currentPaymentDate,
      sanRate: rate
    }, { withCredentials: true });
    
    // 지급일도 업데이트 (기본값으로 설정된 경우)
    if (!paymentDate.value) {
      paymentDate.value = currentPaymentDate;
    }
    
    console.log(`산재보험요율 ${rate} 현재 월(${selectedYearMonth.value})에 자동 저장 완료`);
  } catch (error) {
    console.error('산재보험요율 자동 저장 오류:', error);
  }
};

// 임금지급일 하이라이트
const highlightPaymentDate = () => {
  isPaymentDateHighlighted.value = true;
  // 3초 후 하이라이트 제거
  setTimeout(() => {
    isPaymentDateHighlighted.value = false;
  }, 3000);
};

// 지급일 저장
const handlePaymentDateChange = async () => {
  const user = store.state.user;
  if (!user || !user.m_sisul_code || !selectedYearMonth.value || !paymentDate.value) {
    return;
  }

  try {
    await axios.post('/api/wage-process/payment-date', {
      sisulCode: user.m_sisul_code,
      yearMonth: selectedYearMonth.value,
      paymentDate: paymentDate.value,
      sanRate: sanRate.value
    }, { withCredentials: true });

    store.commit('showSnackbar', {
      text: '💰 임금지급일이 저장되었어요!',
      color: 'success'
    });
  } catch (error) {
    console.error('지급일 저장 오류:', error);
    store.commit('showSnackbar', {
      text: '지급일 저장 중 오류가 발생했어요.',
      color: 'error'
    });
  }
};

// 산재보험요율 저장
const handleSanRateChange = async () => {
  const user = store.state.user;
  if (!user || !user.m_sisul_code || !selectedYearMonth.value || !paymentDate.value) {
    return;
  }

  try {
    await axios.post('/api/wage-process/payment-date', {
      sisulCode: user.m_sisul_code,
      yearMonth: selectedYearMonth.value,
      paymentDate: paymentDate.value,
      sanRate: sanRate.value
    }, { withCredentials: true });

    store.commit('showSnackbar', {
      text: '산재보험요율이 저장되었어요!',
      color: 'success'
    });
  } catch (error) {
    console.error('산재보험요율 저장 오류:', error);
    store.commit('showSnackbar', {
      text: '산재보험요율 저장 중 오류가 발생했어요.',
      color: 'error'
    });
  }
};

// 사원 업로드 열기
const openMemberUpload = () => {
  showMemberUpload.value = true;
};

// 사원 업로드 완료
const handleMemberUploaded = async () => {
  showMemberUpload.value = false;
  // 사원 정보 다시 가져오기
  await fetchEmployees();
  // Wage Process 상태 갱신
  await fetchWageProcessStatus();
  store.commit('showSnackbar', {
    text: '✅ 사원 정보 업로드가 완료되었어요!',
    color: 'success'
  });
  // 자동으로 다음 단계로
  if (step1Completed.value) {
    setTimeout(() => {
      selectedStep.value = 2;
    }, 800);
  }
};

// 청구내역 업로드 완료
const handleBillingUploaded = async (count: number) => {
  uploadedFilesCount.value = count;
  await fetchBillingData();
  // Wage Process 상태 갱신
  await fetchWageProcessStatus();
  if (step2Completed.value) {
    setTimeout(() => {
      selectedStep.value = 3;
    }, 800);
  }
};

// 급여 설정으로 이동
const goToPayrollSettings = () => {
  step3Completed.value = true;
  store.commit('showSnackbar', {
    text: '✅ 급여 설정 단계를 완료했어요!',
    color: 'success'
  });
  setTimeout(() => {
    selectedStep.value = 4;
  }, 300);
};

// 임금관리결과로 이동
const goToPayrollResult = () => {
  step4Completed.value = true;
  router.push('/pgm/payroll');
};

const ensureWageProcessInitialized = async () => {
  const user = store.state.user;
  if (!user || !user.m_sisul_code) return;
  if (wageProcessStatus.value) return;
  try {
    await axios.post(
      '/api/wage-process/init',
      {
        sisulCode: user.m_sisul_code,
        yearMonth: selectedYearMonth.value
      },
      { withCredentials: true }
    );
  } catch (error) {
    console.error('Wage Process 초기화 오류:', error);
  }
};

const confirmWageStep = async (step: number) => {
  const user = store.state.user;
  if (!user || !user.m_sisul_code || confirmingStep.value !== null) {
    return;
  }
  confirmingStep.value = step;
  try {
    await ensureWageProcessInitialized();
    await axios.post(
      '/api/wage-process/confirm-step',
      {
        sisulCode: user.m_sisul_code,
        yearMonth: selectedYearMonth.value,
        step
      },
      { withCredentials: true }
    );
    await fetchWageProcessStatus();
    store.commit('showSnackbar', {
      text: `✅ ${step}단계를 확정했어요!`,
      color: 'success'
    });
    const nextStep = step + 1;
    if (nextStep <= totalSteps && canAccessStep(nextStep)) {
      selectedStep.value = nextStep;
    }
  } catch (error: any) {
    const message = error.response?.data?.message || '단계 확정 중 오류가 발생했어요.';
    store.commit('showSnackbar', {
      text: message,
      color: 'error'
    });
  } finally {
    confirmingStep.value = null;
  }
};

const releaseWageStep = async (step: number) => {
  const user = store.state.user;
  if (!user || !user.m_sisul_code || confirmingStep.value !== null) {
    return;
  }
  confirmingStep.value = step;
  try {
    await axios.post(
      '/api/wage-process/release-step',
      {
        sisulCode: user.m_sisul_code,
        yearMonth: selectedYearMonth.value,
        step
      },
      { withCredentials: true }
    );
    await fetchWageProcessStatus();
    store.commit('showSnackbar', {
      text: `🔓 ${step}단계 확정을 해제했어요.`,
      color: 'info'
    });
  } catch (error: any) {
    const message = error.response?.data?.message || '단계 해제 중 오류가 발생했어요.';
    store.commit('showSnackbar', {
      text: message,
      color: 'error'
    });
  } finally {
    confirmingStep.value = null;
  }
};

onMounted(async () => {
  await fetchWageProcessStatus();
  await fetchEmployees();
  await fetchBillingData();
  await fetchPaymentDate();
});

watch(selectedYearMonth, async (newValue, oldValue) => {
  if (!newValue || newValue === oldValue || newValue.length !== 6) {
    return;
  }

  const year = Number(newValue.substring(0, 4));
  const month = Number(newValue.substring(4, 6));

  if (Number.isNaN(year) || Number.isNaN(month)) {
    return;
  }

  billingYear.value = year;
  billingMonth.value = month;
  
  // 연월이 변경되면 Wage Process 상태부터 조회
  await fetchWageProcessStatus();
  await fetchBillingData();
  await fetchPaymentDate();
});
</script>

<style scoped lang="scss">
@import '@/assets/styles/wageProcess.scss';
</style>
